import type { ProductDetail } from "@viajero/proto/guest/v1";

// 人数の制約は 2 系統ある。商品 (JPT 同期) の枚数下限・上限 stockNumberLimit* と、
// 店舗 (Manager > TableCheck 店舗情報管理) の予約可能人数 shop*PartySize で、
// いずれも 0 は「未設定 = その側の制限なし」を表す。
type PartySizeLimits = Pick<
  ProductDetail,
  "stockNumberLimitMin" | "stockNumberLimitMax" | "shopMinPartySize" | "shopMaxPartySize"
>;

// PartySizeRange は上記 2 系統を合成した実効範囲。min / max は 0 が「制限なし」。
// unavailable は下限が上限を上回り、選べる人数が存在しない状態 (店舗設定の誤りなど)。
export type PartySizeRange = {
  min: number;
  max: number;
  unavailable: boolean;
};

// 商品側の制限は絶対で、店舗設定は範囲を狭める方向にのみ効かせる
// (例: 商品 2〜4 名 / 店舗 1〜3 名 → 実効 2〜3 名)。
export function resolvePartySizeRange(product: PartySizeLimits): PartySizeRange {
  const min = Math.max(product.stockNumberLimitMin, product.shopMinPartySize);
  const max = narrowerMax(product.stockNumberLimitMax, product.shopMaxPartySize);
  return { min, max, unavailable: max > 0 && min > max };
}

// 未設定 (0) 側は無視して厳しい方の上限を採る。
function narrowerMax(productMax: number, shopMax: number): number {
  if (productMax === 0) return shopMax;
  if (shopMax === 0) return productMax;
  return Math.min(productMax, shopMax);
}

// 上限が未設定の場合に使う人数セレクトの選択肢上限。
export const DEFAULT_MAX_QUANTITY = 9;

// 人数セレクトに並べる選択肢の上限。予約不可の状態では 0 のみ選べるようにする。
// 上限が未設定でも下限が既定の選択肢上限を超えることがある (店舗の下限は 30 まで設定できる)
// ため、その場合は下限まで選択肢を広げて有効な人数に到達できるようにする。
export function selectableMaxQuantity(range: PartySizeRange): number {
  if (range.unavailable) return 0;
  if (range.max > 0) return range.max;
  return Math.max(range.min, DEFAULT_MAX_QUANTITY);
}
